import type { FileMap } from "../stores/fileStore/types";
import { collectXacroIncludeDependenciesFromFileTree, expandXacroFile } from "./xacroExpansion";

export type MjcfUploadDependency = {
  kind: "include" | "mesh" | "texture" | "hfield";
  path: string;
  source?: "mjcf" | "xacro";
};

type RegisteredUploadAsset = {
  kind?: string;
  id?: string;
  name?: string;
  hash?: string;
  sources?: {
    url?: string;
    idbKey?: string;
    blobUrl?: string;
    libraryPathPrefix?: string;
  };
};

const normalizeAssetPath = (value: string | undefined | null): string => {
  if (!value) return "";
  return String(value)
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\.\/+/, "")
    .replace(/^\/+/, "");
};

const basenameOfPath = (value: string): string => {
  const normalized = normalizeAssetPath(value);
  const parts = normalized.split("/");
  return parts[parts.length - 1] || normalized;
};

const assetKindForDependency = (kind: MjcfUploadDependency["kind"]): "mesh" | "texture" | null => {
  if (kind === "mesh") return "mesh";
  if (kind === "texture") return "texture";
  return null;
};

const registeredAssetSourceKey = (entry: RegisteredUploadAsset): string => {
  const sources = entry.sources || {};
  return (
    (typeof entry.hash === "string" && entry.hash ? `hash:${entry.hash}` : "") ||
    (typeof sources.idbKey === "string" && sources.idbKey ? `idb:${sources.idbKey}` : "") ||
    (typeof sources.url === "string" && sources.url ? `url:${sources.url}` : "") ||
    (typeof sources.blobUrl === "string" && sources.blobUrl ? `blob:${sources.blobUrl}` : "") ||
    (typeof entry.id === "string" && entry.id ? `id:${entry.id}` : "")
  );
};

const registeredAssetCandidatePaths = (entry: RegisteredUploadAsset): string[] => {
  const sources = entry.sources || {};
  const paths = [typeof sources.url === "string" ? sources.url : ""].filter(Boolean);
  const libraryPrefix =
    typeof sources.libraryPathPrefix === "string" ? sources.libraryPathPrefix.trim() : "";
  const entryName = typeof entry.name === "string" ? entry.name.trim().replace(/^\/+/, "") : "";
  if (libraryPrefix && entryName) {
    const prefix = libraryPrefix.replace(/\/+$/, "");
    paths.push(`${prefix}/${entryName}`);
    if (!entryName.includes("/")) {
      paths.push(`${prefix}/assets/${entryName}`);
    }
  }
  return Array.from(new Set(paths));
};

const pathMatchesWithAssetsAlias = (candidate: string, requested: string): boolean => {
  if (candidate === requested) return true;
  if (requested.startsWith("assets/") && candidate === requested.replace(/^assets\//, "")) {
    return true;
  }
  if (!requested.startsWith("assets/") && candidate === `assets/${requested}`) {
    return true;
  }
  return false;
};

const isDependencySatisfiedByFileMap = (
  dependency: MjcfUploadDependency,
  fileMap: FileMap
): boolean => {
  const keys = Object.keys(fileMap || {});
  const normalizedKeys = new Set(keys.map((key) => normalizeAssetPath(key).toLowerCase()));
  const basenames = new Set(keys.map((key) => basenameOfPath(key).toLowerCase()).filter(Boolean));
  const path = normalizeAssetPath(dependency.path);
  const normalized = path.toLowerCase();
  const basename = basenameOfPath(path).toLowerCase();
  if (!normalized) return true;
  if (normalizedKeys.has(normalized)) return true;
  if (normalized.startsWith("assets/") && normalizedKeys.has(normalized.replace(/^assets\//, ""))) {
    return true;
  }
  if (!normalized.startsWith("assets/") && normalizedKeys.has(`assets/${normalized}`)) {
    return true;
  }
  return basenames.has(basename);
};

const hasRegisteredAssetForDependency = (
  dependency: MjcfUploadDependency,
  registeredAssets: RegisteredUploadAsset[]
): boolean => {
  const expectedKind = assetKindForDependency(dependency.kind);
  if (!expectedKind) return false;
  const requested = normalizeAssetPath(dependency.path).toLowerCase();
  if (!requested) return false;

  const matchingEntries = registeredAssets.filter((entry) => {
    if (entry?.kind !== expectedKind) return false;
    const candidates = registeredAssetCandidatePaths(entry).map((path) =>
      normalizeAssetPath(path).toLowerCase()
    );
    return candidates.some((candidate) => pathMatchesWithAssetsAlias(candidate, requested));
  });
  if (matchingEntries.length === 0) return false;
  const sourceKeys = new Set(matchingEntries.map(registeredAssetSourceKey).filter(Boolean));
  return sourceKeys.size <= 1 || matchingEntries.length === 1;
};

const resolveFileFromMap = (path: string, fileMap: FileMap): File | null => {
  const normalized = normalizeAssetPath(path);
  if (!normalized) return null;
  const keys = Object.keys(fileMap || {});
  const lowerNormalized = normalized.toLowerCase();
  const basename = basenameOfPath(normalized).toLowerCase();
  const candidates = keys.filter((key) => {
    const normalizedKey = normalizeAssetPath(key).toLowerCase();
    if (normalizedKey === lowerNormalized) return true;
    if (
      lowerNormalized.startsWith("assets/") &&
      normalizedKey === lowerNormalized.replace(/^assets\//, "")
    ) {
      return true;
    }
    if (!lowerNormalized.startsWith("assets/") && normalizedKey === `assets/${lowerNormalized}`) {
      return true;
    }
    return basenameOfPath(normalizedKey).toLowerCase() === basename;
  });
  const candidateFiles = candidates
    .map((key) => fileMap[key])
    .filter((file): file is File => !!file);
  const uniqueFiles = Array.from(new Set(candidateFiles));
  return uniqueFiles.length === 1 ? uniqueFiles[0] : null;
};

const joinAssetPath = (dir: string, file: string): string => {
  const normalizedFile = normalizeAssetPath(file);
  if (!normalizedFile) return "";
  if (!dir || normalizedFile.startsWith(`${normalizeAssetPath(dir)}/`)) return normalizedFile;
  return `${normalizeAssetPath(dir)}/${normalizedFile}`;
};

const getCompilerAssetDirs = (xmlDoc: Document) => {
  const compiler = xmlDoc.querySelector("compiler");
  return {
    meshdir: normalizeAssetPath(compiler?.getAttribute("meshdir")) || "assets",
    texturedir: normalizeAssetPath(compiler?.getAttribute("texturedir")) || "assets",
  };
};

export function collectMjcfUploadDependenciesFromDocument(
  xmlDoc: Document
): MjcfUploadDependency[] {
  const { meshdir, texturedir } = getCompilerAssetDirs(xmlDoc);
  const dependencies: MjcfUploadDependency[] = [];

  const add = (kind: MjcfUploadDependency["kind"], path: string | null | undefined) => {
    const normalized = normalizeAssetPath(path);
    if (!normalized) return;
    dependencies.push({ kind, path: normalized });
  };

  xmlDoc.querySelectorAll("include").forEach((includeEl) => {
    add("include", includeEl.getAttribute("file"));
  });

  xmlDoc.querySelectorAll("mesh").forEach((meshEl) => {
    const file = meshEl.getAttribute("file") || meshEl.getAttribute("obj");
    add("mesh", joinAssetPath(meshdir, file || ""));
  });

  xmlDoc.querySelectorAll("texture").forEach((textureEl) => {
    const file = textureEl.getAttribute("file");
    add("texture", joinAssetPath(texturedir, file || ""));
  });

  xmlDoc.querySelectorAll("hfield").forEach((hfieldEl) => {
    add("hfield", hfieldEl.getAttribute("file"));
  });

  const seen = new Set<string>();
  return dependencies.filter((dependency) => {
    const key = `${dependency.kind}:${dependency.path.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const normalizeUrdfAssetPath = (value: string | undefined | null): string => {
  const normalized = normalizeAssetPath(value);
  if (normalized.startsWith("package://")) {
    return normalized.slice("package://".length).replace(/^\/+/, "");
  }
  if (normalized.startsWith("file://")) {
    return normalized.slice("file://".length).replace(/^\/+/, "");
  }
  return normalized;
};

export function collectUrdfUploadDependenciesFromDocument(
  xmlDoc: Document
): MjcfUploadDependency[] {
  const dependencies: MjcfUploadDependency[] = [];
  const add = (kind: MjcfUploadDependency["kind"], path: string | null | undefined) => {
    const normalized = normalizeUrdfAssetPath(path);
    if (!normalized) return;
    dependencies.push({ kind, path: normalized });
  };

  xmlDoc.querySelectorAll("mesh").forEach((meshEl) => {
    add("mesh", meshEl.getAttribute("filename"));
  });
  xmlDoc.querySelectorAll("texture").forEach((textureEl) => {
    add("texture", textureEl.getAttribute("filename"));
  });

  const seen = new Set<string>();
  return dependencies.filter((dependency) => {
    const key = `${dependency.kind}:${dependency.path.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function getXmlDocumentKind(xmlDoc: Document): "mjcf" | "urdf" | "unknown" {
  const rootName = xmlDoc.documentElement?.tagName?.toLowerCase() || "";
  if (rootName === "mujoco") return "mjcf";
  if (rootName === "robot") return "urdf";
  return "unknown";
}

const isXacroFileName = (fileName: string | undefined | null): boolean =>
  String(fileName || "")
    .toLowerCase()
    .endsWith(".xacro");

const hasXacroElements = (xmlDoc: Document): boolean =>
  Array.from(xmlDoc.querySelectorAll("*")).some((element) => {
    const tagName = element.tagName.toLowerCase();
    const prefix = element.prefix?.toLowerCase() || "";
    return prefix === "xacro" || tagName.startsWith("xacro:");
  });

export function getMissingMjcfUploadDependencies(
  dependencies: MjcfUploadDependency[],
  fileMap: FileMap,
  registeredAssets: RegisteredUploadAsset[] = []
): MjcfUploadDependency[] {
  return dependencies.filter((dependency) => {
    if (isDependencySatisfiedByFileMap(dependency, fileMap)) return false;
    return !hasRegisteredAssetForDependency(dependency, registeredAssets);
  });
}

export function getRegisteredMjcfUploadDependencies(
  dependencies: MjcfUploadDependency[],
  fileMap: FileMap,
  registeredAssets: RegisteredUploadAsset[] = []
): MjcfUploadDependency[] {
  return dependencies.filter((dependency) => {
    if (isDependencySatisfiedByFileMap(dependency, fileMap)) return false;
    return hasRegisteredAssetForDependency(dependency, registeredAssets);
  });
}

export function formatMjcfMissingDependencies(dependencies: MjcfUploadDependency[]): string[] {
  return dependencies.map((dependency) => {
    if (dependency.kind === "include" && dependency.source === "xacro")
      return `xacro include: ${dependency.path}`;
    if (dependency.kind === "include") return dependency.path;
    return `${dependency.kind}: ${dependency.path}`;
  });
}

export async function collectMjcfUploadDependenciesFromFile(
  xmlFile: File
): Promise<MjcfUploadDependency[]> {
  const text = await xmlFile.text();
  const xmlDoc = new DOMParser().parseFromString(text, "application/xml");
  const parseError = xmlDoc.querySelector("parsererror");
  if (parseError) {
    throw new Error(`XML parsing failed while checking dependencies for ${xmlFile.name}`);
  }
  return collectMjcfUploadDependenciesFromDocument(xmlDoc);
}

export async function collectXmlUploadDependenciesFromFileTree(
  xmlFile: File,
  fileMap: FileMap,
  visited = new Set<string>()
): Promise<MjcfUploadDependency[]> {
  const text = await xmlFile.text();
  const xmlDoc = new DOMParser().parseFromString(text, "application/xml");
  const parseError = xmlDoc.querySelector("parsererror");
  if (parseError) {
    throw new Error(`XML parsing failed while checking dependencies for ${xmlFile.name}`);
  }
  const kind = getXmlDocumentKind(xmlDoc);
  if (isXacroFileName(xmlFile.name) || hasXacroElements(xmlDoc)) {
    const includeDependencies = await collectXacroIncludeDependenciesFromFileTree(xmlFile, fileMap);
    const expanded = await expandXacroFile(xmlFile, fileMap);
    const expandedText = await expanded.urdfFile.text();
    const expandedDoc = new DOMParser().parseFromString(expandedText, "application/xml");
    const expandedParseError = expandedDoc.querySelector("parsererror");
    if (expandedParseError) {
      throw new Error(`XML parsing failed while checking xacro dependencies for ${xmlFile.name}`);
    }
    const dependencies = [
      ...includeDependencies,
      ...collectUrdfUploadDependenciesFromDocument(expandedDoc),
    ];
    const seen = new Set<string>();
    return dependencies.filter((dependency) => {
      const key = `${dependency.kind}:${dependency.path.toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
  if (kind === "urdf") return collectUrdfUploadDependenciesFromDocument(xmlDoc);
  return collectMjcfUploadDependenciesFromFileTree(xmlFile, fileMap, visited);
}

export async function collectMjcfUploadDependenciesFromFileTree(
  xmlFile: File,
  fileMap: FileMap,
  visited = new Set<string>()
): Promise<MjcfUploadDependency[]> {
  const key = `${xmlFile.name}:${xmlFile.size}:${xmlFile.lastModified}`;
  if (visited.has(key)) return [];
  visited.add(key);

  const dependencies = await collectMjcfUploadDependenciesFromFile(xmlFile);
  const nestedDependencies: MjcfUploadDependency[] = [];
  for (const dependency of dependencies) {
    if (dependency.kind !== "include") continue;
    const includeFile = resolveFileFromMap(dependency.path, fileMap);
    if (!includeFile) continue;
    nestedDependencies.push(
      ...(await collectMjcfUploadDependenciesFromFileTree(includeFile, fileMap, visited))
    );
  }

  const seen = new Set<string>();
  return [...dependencies, ...nestedDependencies].filter((dependency) => {
    const dedupeKey = `${dependency.kind}:${dependency.path.toLowerCase()}`;
    if (seen.has(dedupeKey)) return false;
    seen.add(dedupeKey);
    return true;
  });
}

export async function expandMjcfIncludesToFile(
  xmlFile: File,
  fileMap: FileMap,
  visited = new Set<string>()
): Promise<File> {
  const key = `${xmlFile.name}:${xmlFile.size}:${xmlFile.lastModified}`;
  if (visited.has(key)) {
    throw new Error(`Cyclic MJCF include detected at ${xmlFile.name}`);
  }
  visited.add(key);

  const text = await xmlFile.text();
  const xmlDoc = new DOMParser().parseFromString(text, "application/xml");
  const parseError = xmlDoc.querySelector("parsererror");
  if (parseError) {
    throw new Error(`XML parsing failed while expanding includes for ${xmlFile.name}`);
  }

  const includeElements = Array.from(xmlDoc.querySelectorAll("include"));
  for (const includeElement of includeElements) {
    const includePath = normalizeAssetPath(includeElement.getAttribute("file"));
    const includeFile = resolveFileFromMap(includePath, fileMap);
    if (!includeFile) continue;
    const expandedIncludeFile = await expandMjcfIncludesToFile(includeFile, fileMap, visited);
    const includeText = await expandedIncludeFile.text();
    const includeDoc = new DOMParser().parseFromString(includeText, "application/xml");
    const includeParseError = includeDoc.querySelector("parsererror");
    if (includeParseError) {
      throw new Error(`XML parsing failed while expanding include ${includePath}`);
    }

    const sourceNodes =
      includeDoc.documentElement?.tagName?.toLowerCase() === "mujoco"
        ? Array.from(includeDoc.documentElement.childNodes)
        : includeDoc.documentElement
          ? [includeDoc.documentElement]
          : [];
    const importedNodes = sourceNodes.map((node) => xmlDoc.importNode(node, true));
    includeElement.replaceWith(...importedNodes);
  }

  visited.delete(key);
  const expandedXml = new XMLSerializer().serializeToString(xmlDoc);
  return new File([expandedXml], xmlFile.name, { type: xmlFile.type || "application/xml" });
}
